<template>
    <div>
        <h4 class="text-slate-800 dark:text-slate-100 text-xl font-semibold">
            Two Factor Authentication (2FA)
        </h4>
        <p class="text-slate-500 dark:text-slate-400 text-sm mb-8">
            Two Factor Authentication (2FA) adds an extra layer of security to
            your My10x account by using additional step when you sign in.
            <br />
            Only one can be used at time.
        </p>
        <ul>
            <li class="flex flex-col lg:flex-row justify-between py-2 lg:py-4">
                <div class="flex flex-col grow">
                    <div class="mb-2">
                        <h5
                            class="text-slate-800 dark:text-slate-100 text-sm font-medium"
                            id="privacy-option-1-label"
                        >
                            Through Google Authenticator App (TOTP)
                        </h5>
                        <p class="text-slate-500 dark:text-slate-400 text-sm">
                            Use an app to receive a temporary one-time passcode
                            each time you log in.
                        </p>
                    </div>

                    <div
                        class="w-full p-6 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700"
                        v-if="confirmPassword || showQrCode"
                    >
                        <div v-if="confirmPassword">
                            <h5
                                class="text-slate-800 dark:text-slate-100 text-base font-medium mb-4 leading-tight"
                            >
                                Verification Required
                            </h5>
                            <p class="text-sm text-gray-500 dark:text-gray-400 mb-3">
                                To verify your identity, please enter your
                                password.
                            </p>
                            <input
                                type="password"
                                class="w-60 form-input placeholder:text-left mb-6 placeholder:text-light"
                                :class="{
                                    'border-red-500': hasPasswordError,
                                }"
                                v-model="password"
                                placeholder="Password"
                            />
                            <Errors :errors="errors" class="mb-4" />
                            <div class="flex items-center gap-4">
                                <ActionButton
                                    @clicked="
                                        actionMode === 'enable'
                                            ? twoFactorStart()
                                            : disableTwoFactor()
                                    "
                                    label="Verify"
                                    classes="bg-slate-800 hover:bg-slate-600 text-sm px-4 py-2.5 rounded shadow transition-colors text-white font-medium"
                                />

                                <button
                                    class="flex items-center gap-2 justify-center px-4 py-2.5 bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                    @click="cancelPasswordVerification()"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                        <div v-if="showQrCode && !hideVerifyCodeActions">
                            <h6
                                class="text-slate-800 dark:text-slate-100 text-base font-medium mb-4"
                            >
                                Setup Two Factor Authentication through Google
                                Authenticator App
                            </h6>
                            <p
                                class="text-slate-800 dark:text-slate-100 text-base font-medium mb-2"
                            >
                                1. Scan the QR code
                            </p>

                            <p class="text-slate-800 dark:text-slate-100 text-sm mb-4">
                                Scan this QR code in your authenticator app.
                                <br />
                                We prefer
                                <a
                                    href="https://support.google.com/accounts/answer/1066447?hl=en&co=GENIE.Platform%3DiOS&sjid=5477005522162596228-NA"
                                    class="text-slate-500 dark:text-slate-400 font-medium"
                                    target="_blank"
                                >
                                    Google Authenticator app
                                </a>
                                .
                            </p>
                            <SafeQrCode
                                class="w-[192px] h-[192px] mb-5"
                                :svg="qrCode"
                            />
                            <p
                                class="text-slate-800 dark:text-slate-100 text-base font-medium mb-2"
                            >
                                2. Enter confirmation code
                            </p>
                            <p class="text-slate-800 dark:text-slate-100 text-sm mb-4">
                                Enter the code shown in your authenticator app
                            </p>
                            <div class="w-[400px]">
                                <VOtpInput
                                    ref="otpInput"
                                    input-classes="otp-input"
                                    inputType="numeric"
                                    :num-inputs="6"
                                    v-model:value="otpCode"
                                    :should-auto-focus="true"
                                    :should-focus-order="true"
                                    @on-complete="verifyCode"
                                    class="mb-5"
                                />
                            </div>
                            <Errors :errors="errors" class="mb-4" />
                            <div class="flex items-center gap-4">
                                <ActionButton
                                    @clicked="verifyCode"
                                    label="Verify"
                                    :loading="loading"
                                    classes="btn bg-navy-500 hover:bg-navy-400 text-white"
                                />
                                <button
                                    class="flex items-center gap-2 justify-center px-4 py-2.5 bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                    @click="cancelPasswordVerification()"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                        <div class="w-[380px]" v-if="hideVerifyCodeActions">
                            <p class="text-slate-800 dark:text-slate-100 text-sm font-medium mb-6">
                                All done! You have confirmed your two factor
                                authentication.
                            </p>

                            <p class="text-slate-800 dark:text-slate-100 text-sm mb-4">
                                Please copy these recovery codes in case you
                                ever need to recover your account and you no
                                longer have access to your authenticator app.
                            </p>

                            <div
                                class="pb-1 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden mb-2"
                            >
                                <div
                                    class="flex items-start p-3 gap-2 bg-amber-50"
                                >
                                    <Icon
                                        icon="caution"
                                        class="text-amber-500 mt-0.5"
                                    />
                                    <p
                                        class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-tight"
                                    >
                                        You will only see these codes once.
                                        <br />
                                        Store them in a safe place.
                                    </p>
                                </div>
                                <ul
                                    class="flex flex-wrap py-4 px-2 gap-2 bg-white dark:bg-slate-800"
                                >
                                    <li
                                        class="w-[48%] text-slate-600 dark:text-slate-300 font-mono text-xs text-center"
                                        v-for="code in recoveryCodes"
                                    >
                                        {{ code }}
                                    </li>
                                </ul>
                            </div>

                            <div
                                class="flex items-center justify-between gap-4"
                            >
                                <button
                                    class="w-full flex items-center gap-2 justify-center px-4 py-2.5 bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                    @click="downloadRecoveryCodes()"
                                >
                                    <Icon icon="download" />
                                    Download .txt
                                </button>
                                <button
                                    class="w-full flex items-center gap-2 justify-center px-4 py-2.5 bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                    @click="copyCodes()"
                                >
                                    <Icon icon="copy" />
                                    <span v-if="!copied">Copy</span>
                                    <span v-else>Copied!</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="relative mt-5">
                    <button
                        type="button"
                        class="relative lg:ml-4 inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
                        @click="toggleTwoFactorInput"
                        :class="{
                            'bg-orange': twoFactor,
                            'bg-gray-200': !twoFactor,
                        }"
                        role="switch"
                        aria-checked="true"
                        aria-labelledby="privacy-option-1-label"
                        aria-describedby="privacy-option-1-description"
                    >
                        <input
                            type="checkbox"
                            class="hidden"
                            v-model="twoFactor"
                            name="two_factor"
                            value="1"
                        />
                        <span
                            aria-hidden="true"
                            class="inline-block h-5 w-5 transform rounded-full bg-white dark:bg-slate-800 shadow ring-0 transition duration-200 ease-in-out"
                            :class="{
                                'translate-x-5': twoFactor,
                                'translate-x-0': !twoFactor,
                            }"
                        ></span>
                        <i
                            class="fa-solid fa-check fa-xs text-orange absolute top-2.5 right-1 transition"
                            :class="{
                                'opacity-100 delay-300': twoFactor,
                                'opacity-0': !twoFactor,
                            }"
                        ></i>
                    </button>
                </div>
            </li>
            <li class="flex flex-col lg:flex-row justify-between py-2 lg:py-4">
                <div class="flex flex-col grow">
                    <div class="mb-2 lg:mb-8">
                        <h5
                            class="text-slate-800 dark:text-slate-100 text-sm font-medium"
                            id="privacy-option-1-label"
                        >
                            Use your phone number
                        </h5>
                        <p class="text-slate-500 dark:text-slate-400 text-sm">
                            Receive a one-time passcode via SMS each time you
                            log in.
                        </p>
                    </div>

                    <div
                        class="w-full p-6 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700"
                        v-if="
                            confirmSMSPassword ||
                            showCodeInput ||
                            showSmsAgreement
                        "
                    >
                        <div
                            v-if="
                                confirmSMSPassword &&
                                !showCodeInput &&
                                !showSmsAgreement
                            "
                        >
                            <h5
                                class="text-slate-800 dark:text-slate-100 text-base font-medium mb-4 leading-tight"
                            >
                                Verification Required
                            </h5>
                            <p class="text-sm text-gray-500 dark:text-gray-400 mb-3">
                                To verify your identity, please enter your
                                password.
                            </p>
                            <input
                                type="password"
                                class="w-60 form-input placeholder:text-left mb-6 placeholder:text-light"
                                :class="{
                                    'border-red-500': hasPasswordError,
                                }"
                                v-model="password"
                                placeholder="Password"
                            />
                            <Errors :errors="errors" class="mb-4" />
                            <div class="flex items-center gap-4">
                                <ActionButton
                                    @clicked="
                                        actionMode === 'enable'
                                            ? startEnableSmsTwoFactor()
                                            : disableSmsTwoFactor()
                                    "
                                    label="Verify"
                                    classes="bg-slate-800 hover:bg-slate-600 text-sm px-4 py-2.5 rounded shadow transition-colors text-white font-medium"
                                />

                                <button
                                    class="flex items-center gap-2 justify-center px-4 py-2.5 bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                    @click="cancelSMSPasswordVerification()"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>

                        <div v-if="showSmsAgreement && !showCodeInput">
                            <h6
                                class="text-slate-800 dark:text-slate-100 text-base font-medium mb-4"
                            >
                                Setup Two Factor Authentication through Phone
                                Number
                            </h6>

                            <!--
                            <p class="text-slate-800 dark:text-slate-100 text-sm mb-4">
                                Enter the phone number where you'd like to
                                receive authentication codes each time you log
                                in.
                            </p>
                        -->
                            <label class="flex items-start gap-2 mb-6">
                                <input
                                    type="checkbox"
                                    v-model="twofaAgreed"
                                    value="1"
                                    class="form-checkbox mt-1"
                                />
                                <p class="text-slate-800 dark:text-slate-100 text-xs w-80">
                                    I agree to receive messages from My10x at
                                    the phone number provided above for the
                                    purpose of two factor authentication. I
                                    understand I will receive messages anytime I
                                    login and I have 2FA enabled and data rates
                                    may apply. Reply STOP to opt out.
                                </p>
                            </label>
                            <Errors :errors="errors" class="mb-4" />
                            <div class="flex items-center gap-4">
                                <ActionButton
                                    @clicked="resendCode()"
                                    label="Send code"
                                    classes="bg-slate-800 hover:bg-slate-600 text-sm px-4 py-2.5 rounded shadow transition-colors text-white font-medium"
                                    :disabled="!twofaAgreed"
                                />

                                <button
                                    class="flex items-center gap-2 justify-center px-4 py-2.5 bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                    @click="cancelSMSPasswordVerification()"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>

                        <div v-if="showCodeInput">
                            <h6
                                class="text-slate-800 dark:text-slate-100 text-base font-medium mb-4"
                            >
                                Setup Two Factor Authentication through Phone
                                Number
                            </h6>

                            <p class="text-sm mb-4">
                                Enter the verification code we sent to phone
                                number:
                                <br />
                                {{ phone }}.
                            </p>

                            <div class="w-[400px]">
                                <VOtpInput
                                    ref="otpInput2"
                                    input-classes="otp-input"
                                    inputType="numeric"
                                    :num-inputs="6"
                                    v-model:value="code"
                                    :should-auto-focus="true"
                                    :should-focus-order="true"
                                    @on-complete="submitCode"
                                    class="mb-5"
                                />
                            </div>

                            <p class="text-slate-800 dark:text-slate-100 text-sm mb-6">
                                Didn't get your code?
                                <button
                                    class="font-medium opacity-50"
                                    @click="resendCode()"
                                >
                                    Send a new one
                                </button>
                            </p>

                            <Errors :errors="errors" class="mb-4" />
                            <div class="flex items-center gap-4">
                                <ActionButton
                                    v-if="!phone_verified"
                                    @clicked="submitCode"
                                    label="Verify"
                                    :loading="sendingCode"
                                    :error="hasPasswordError"
                                    classes="btn bg-navy-500 hover:bg-navy-400 text-white !w-36"
                                />
                                <button
                                    class="flex items-center gap-2 justify-center px-4 py-2.5 bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                    @click="cancelSMSPasswordVerification()"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="relative mt-5">
                    <button
                        type="button"
                        class="relative lg:ml-4 inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
                        @click="toggleInput"
                        :class="{
                            'bg-orange': twofa_enabled,
                            'bg-gray-200': !twofa_enabled,
                        }"
                        role="switch"
                        aria-checked="true"
                        aria-labelledby="privacy-option-1-label"
                        aria-describedby="privacy-option-1-description"
                    >
                        <input
                            type="checkbox"
                            class="hidden"
                            v-model="twofa_enabled"
                            name="twofa_enabled"
                            value="1"
                        />
                        <span
                            aria-hidden="true"
                            class="inline-block h-5 w-5 transform rounded-full bg-white dark:bg-slate-800 shadow ring-0 transition duration-200 ease-in-out"
                            :class="{
                                'translate-x-5': twofa_enabled,
                                'translate-x-0': !twofa_enabled,
                            }"
                        ></span>
                        <i
                            class="fa-solid fa-check fa-xs text-orange absolute top-2.5 right-1 transition"
                            :class="{
                                'opacity-100 delay-300': twofa_enabled,
                                'opacity-0': !twofa_enabled,
                            }"
                        ></i>
                    </button>
                </div>
            </li>
        </ul>
    </div>
</template>
<script>
import { mapState } from 'pinia';
import { useAppStore } from '@/store/app';

import Auth from '@/services/auth';
import Errors from '@/components/errors.vue';
import ActionButton from '@/components/action-button.vue';
import { vMaska } from 'maska/vue';
import VOtpInput from 'vue3-otp-input';
import Icon from '@/components/ui/icon/icon.vue';
import { notify } from 'notiwind';
import { handleError } from '@/utils/error-handler';


export default {
    data: () => ({
        errors: [],
        loading: false,
        showCodeInput: false,
        showResendCode: false,
        sendingCode: false,
        resending: false,
        initial2faSmsEnabled: false,
        initial2faTotpEnabled: false,
        twofa_enabled: false,
        twoFactor: false,
        code: '',
        phone_verified: false,
        otpCode: '',
        showQrCode: false,
        qrCode: '',
        hideVerifyCodeActions: false,
        recoveryCodes: [],
        confirmSMSPassword: false,
        confirmPassword: false,
        password: null,
        actionMode: '',
        copied: false,
        downloaded: false,
        showSmsAgreement: false,
        twofaAgreed: false,
        hasPasswordError: false,
    }),
    directives: { maska: vMaska },
    components: {
        ActionButton,
        Errors,
        VOtpInput,
        Icon,
    },
    computed: {
        ...mapState(useAppStore, ['phone', 'userSettings']),
    },
    methods: {
        toggleInput() {
            this.twofa_enabled = !this.twofa_enabled;

            if (this.twoFactor) {
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Only one type of 2FA can be enabled at a time.',
                    },
                    5000
                );
                this.twofa_enabled = false;
                return;
            }

            if (this.twofa_enabled && !this.phone_verified) {
                if (
                    !this.userSettings.phone ||
                    this.userSettings.phone.length !== 14
                ) {
                    notify(
                        {
                            group: 'error',
                            title: 'Phone Number',
                            text: 'Please enter a valid phone number.',
                        },
                        5000
                    );
                    this.twofa_enabled = false;
                    return false;
                }
            }

            this.confirmSMSPassword = true;

            if (this.initial2faSmsEnabled === this.twofa_enabled) {
                this.confirmSMSPassword = false;
            }

            if (this.twofa_enabled) {
                this.actionMode = 'enable';
            }

            if (!this.twofa_enabled) {
                this.actionMode = 'disable';
            }
        },
        disableSmsTwoFactor() {
            this.errors = [];
            if (this.password && this.password.length > 5) {
                Auth.confirmPassword(this.password)
                    .then((response) => {
                        if (response.status < 202) {
                                axios
                                    .post('/2fa/disable', { password: this.password })
                                    .then(() => {
                                    notify(
                                        {
                                            group: 'success',
                                            title: '2FA Disabled',
                                            text: '2FA is now disabled.',
                                        },
                                        6000
                                    );
                                    this.phone_verified = false;
                                    this.confirmSMSPassword = false;
                                    this.showCodeInput = false;
                                    this.showSmsAgreement = false;
                                    this.twofaAgreed = false;
                                    this.initial2faSmsEnabled = false;
                                })
                                .catch((response) => {
                                    handleError(response);
                                });
                        } else {
                            this.hasPasswordError = true;
                            this.errors.push({
                                message:
                                    response.response &&
                                    response.response.data &&
                                    response.response.data.message
                                        ? response.response.data.message
                                        : 'An unexpected error occurred. Please try again.',
                            });
                        }
                    })
                    .catch((e) => {
                        handleError(e);
                    });
            }
        },
        startEnableSmsTwoFactor() {
            this.hasPasswordError = false;
            this.errors = [];
            if (this.password && this.password.length > 5) {
                Auth.confirmPassword(this.password)
                    .then((response) => {
                        if (response.status < 202) {
                            this.showSmsAgreement = true;
                            this.password = null;
                        } else {
                            this.hasPasswordError = true;
                            this.errors.push({
                                message:
                                    response.response &&
                                    response.response.data &&
                                    response.response.data.message
                                        ? response.response.data.message
                                        : 'An unexpected error occurred. Please try again.',
                            });
                        }
                    })
                    .catch((e) => {
                        handleError(e);
                    });
            }
        },
        toggleTwoFactorInput() {
            this.twoFactor = !this.twoFactor;
            this.errors = [];

            if (this.twofa_enabled) {
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Only one type of 2Fa can be enabled at a time.',
                    },
                    5000
                );
                this.twoFactor = false;
                return;
            }

            this.confirmPassword = true;

            if (this.initial2faTotpEnabled === this.twoFactor) {
                this.confirmPassword = false;
            }

            if (!this.twoFactor) {
                this.actionMode = 'disable';
                this.showQrCode = false;
                this.hideVerifyCodeActions = false;
                this.qrCode = '';
                this.recoveryCodes = [];
                this.password = null;
            }

            if (this.twoFactor) {
                this.actionMode = 'enable';
            }
        },
        twoFactorStart() {
            this.hasPasswordError = false;
            this.errors = [];
            if (this.password && this.password.length > 5) {
                Auth.confirmFortifyPassword(this.password)
                    .then((response) => {
                        if (response.status <= 201) {
                            Auth.enableTwoFactor(this.password)
                                .then((response) => {
                                    this.getQrCode();
                                    this.confirmPassword = false;
                                    this.password = null;
                                })
                                .catch((e) => {
                                    this.errors.push({
                                        message:
                                            e.response &&
                                            e.response.data &&
                                            e.response.data.message
                                                ? e.response.data.message
                                                : 'An unexpected error occurred. Please try again.',
                                    });
                                });
                        } else {
                            this.hasPasswordError = true;
                            this.errors.push({
                                message: response.response?.data?.message,
                            });
                        }
                    })
                    .catch((e) => {
                        handleError(e);
                    });
            }
        },
        cancelSMSPasswordVerification() {
            this.confirmSMSPassword = false;
            this.showCodeInput = false;
            this.showSmsAgreement = false;
            this.twofaAgreed = false;
            this.password = null;
            this.errors = [];

            if (this.actionMode === 'enable') {
                this.twofa_enabled = false;
            } else {
                this.twofa_enabled = true;
            }
        },
        cancelPasswordVerification() {
            this.confirmPassword = false;
            this.password = null;
            this.errors = [];
            this.showQrCode = false;
            this.hideVerifyCodeActions = false;

            if (this.actionMode === 'enable') {
                this.twoFactor = false;
            } else {
                this.twoFactor = true;
            }
        },
        getQrCode() {
            Auth.get2faQrCode(this.password)
                .then((response) => {
                    this.showQrCode = true;
                    this.qrCode = response.data.svg;
                })
                .catch((e) => {
                    this.errors.push({
                        message:
                            e.response &&
                            e.response.data &&
                            e.response.data.message
                                ? e.response.data.message
                                : 'An unexpected error occurred. Please try again.',
                    });
                });
        },
        verifyCode() {
            this.errors = [];
            Auth.verify2faQrCode(this.otpCode)
                .then((response) => {
                    this.hideVerifyCodeActions = true;
                    this.loading = false;
                    Auth.getRecoveryCodes()
                        .then((response) => {
                            this.recoveryCodes = response.data;
                            this.password = null;
                            this.initial2faTotpEnabled = true;
                            notify(
                                {
                                    group: 'success',
                                    title: '2FA Enabled',
                                    text: 'Two factor authentication is now enabled.',
                                },
                                3500
                            );
                        })
                        .catch((e) => {
                            this.errors.push({
                                message:
                                    e.response &&
                                    e.response.data &&
                                    e.response.data.message
                                        ? e.response.data.message
                                        : 'An unexpected error occurred. Please try again.',
                            });
                        });
                })
                .catch((e) => {
                    this.errors.push({ message: e.response.data.message });
                    this.loading = false;
                    this.$refs.otpInput.clearInput();
                });
        },
        disableTwoFactor() {
            this.errors = [];
            if (this.password && this.password.length > 5) {
                Auth.confirmFortifyPassword(this.password)
                    .then((response) => {
                        if (response.status < 202) {
                            const auth = Auth.disableTwoFactor();
                            auth.then(() => {
                                this.password = null;
                                this.confirmPassword = false;
                                this.initial2faTotpEnabled = false;
                                notify(
                                    {
                                        group: 'success',
                                        title: '2FA Disabled',
                                        text: 'Two factor authentication is now disabled.',
                                    },
                                    3500
                                );
                            }).catch((e) => {
                                this.errors.push({
                                    message:
                                        e.response &&
                                        e.response.data &&
                                        e.response.data.message
                                            ? e.response.data.message
                                            : 'An unexpected error occurred. Please try again.',
                                });
                            });
                        } else {
                            this.hasPasswordError = true;
                            this.errors.push({
                                message:
                                    response.response &&
                                    response.response.data &&
                                    response.response.data.message
                                        ? response.response.data.message
                                        : 'An unexpected error occurred. Please try again.',
                            });
                        }
                    })
                    .catch((e) => {
                        handleError(e);
                    });
            }
        },
        load2FAdata() {
            axios
                .get('/api/load2FAdata')
                .then((response) => {
                    this.twofa_enabled = response.data.twofa_enabled;
                    this.twoFactor = response.data.two_factor;
                    this.phone_verified = response.data.phone_verified;

                    this.initial2faSmsEnabled = response.data.twofa_enabled;
                    this.initial2faTotpEnabled = response.data.two_factor;
                })
                .catch(() => {});
        },
        submitCode() {
            if (
                this.userSettings.phone &&
                this.userSettings.phone.length !== 14
            ) {
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Please enter a valid phone number.',
                    },
                    5000
                );
                this.$refs.phone.classList.add('border-rose-700');
                this.sendingCode = false;
                return false;
            }
            this.showResendCode = false;
            this.resending = false;
            this.errors = [];
            this.sendingCode = true;
            this.validateCode();
            if (this.code.toString().length === 6) {
                const auth = Auth.submitTwoFA({
                    code: this.code,
                    enableTwoFA: true,
                    phone: this.userSettings.phone,
                });
                auth.then((response) => {
                    if (!response.data.valid_code) {
                        let message =
                            'The code is not valid. You may resend it.';
                        if (response.data.message === 'please_wait') {
                            message =
                                'Too many attempts. Please wait 5 min before trying again.';
                            this.twofa_enabled = false;
                            this.showCodeInput = false;
                        }
                        if (response.data.message === 'too_many_tries') {
                            message =
                                'Account is locked. Too many failed attempts.';
                            this.twofa_enabled = false;
                            this.showCodeInput = false;
                        }
                        this.errors.push({ message: message });
                        this.code = '';
                        this.$refs.otpInput2.clearInput();
                        this.showResendCode = true;
                        this.sendingCode = false;
                    } else {
                        notify(
                            {
                                group: 'success',
                                title: '2FA Enabled',
                                text: '2FA is now enabled.',
                            },
                            6000
                        );
                        this.code = '';
                        this.$refs.otpInput2.clearInput();
                        this.showResendCode = true;
                        this.sendingCode = false;
                        // Session-cookie auth; no bearer token to persist
                        // (SUPPORT-663 / F-024).
                        this.confirmSMSPassword = false;
                        this.showCodeInput = false;
                        this.showSmsAgreement = false;
                        this.initial2faSmsEnabled = true;
                    }
                }).catch((e) => {
                    this.handleError(e.response);
                    this.sendingCode = false;
                });
            }
        },
        resendCode() {
            this.showCodeInput = true;
            //this.$refs.otpInput2.clearInput();

            if (
                !this.userSettings.phone ||
                this.userSettings.phone.length !== 14
            ) {
                notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Please enter a valid phone number.',
                    },
                    5000
                );
                this.$refs.phone.classList.add('border-rose-700');
                this.twofa_enabled = false;
                return false;
            }
            this.resending = true;
            const auth = Auth.resendTwoFA();
            auth.then((response) => {
                if (!response.data.success) {
                    let message = '';
                    if (response.data.message === 'please_wait') {
                        message =
                            'Too many attempts. Please wait 5 min before trying again.';
                        this.twofa_enabled = false;
                        this.showCodeInput = false;
                    }
                    if (response.data.message === 'too_many_tries') {
                        message =
                            'Account is locked. Too many failed attempts.';
                        this.twofa_enabled = false;
                        this.showCodeInput = false;
                    }
                    this.code = '';
                    this.showResendCode = false;
                    this.errors.push({ message: message });
                } else {
                    this.code = '';
                    notify(
                        {
                            group: 'success',
                            title: '2FA Code Sent',
                            text: 'We sent you a 2FA code to your phone number to verify.',
                        },
                        6000
                    );

                    this.errors = [];
                }
            }).catch((e) => {
                this.handleError(e.response);
            });
        },
        async copyCodes() {
            const str = this.recoveryCodes.join('\n');

            if (
                navigator &&
                navigator.clipboard &&
                navigator.clipboard.writeText
            ) {
                this.copied = true;
                return navigator.clipboard.writeText(str);
            }
            return Promise.reject('The Clipboard API is not available.');
        },
        downloadRecoveryCodes() {
            const str = this.recoveryCodes.join('\n');
            const blob = new Blob([str], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'my10x_recovery_codes.txt';
            a.click();
            window.URL.revokeObjectURL(url);
            this.downloaded = true;
        },
        validateCode() {
            if (this.code.toString().length !== 6) {
                notify(
                    {
                        group: 'error',
                        title: '2FA Code Invalid',
                        text: 'The SMS code need to be 6 character long.',
                    },
                    5000
                );
            }
        },
        checkLength() {
            if (this.code.toString().length > 6) {
                this.code = this.code.toString().slice(0, 6);
            }
        },
    },
    mounted() {
        this.load2FAdata();
    },
};
</script>
